import { z } from '@awpmarine/core/zod';
import { defineI18n } from 'fumadocs-core/i18n';
import { type LoaderPlugin, loader } from 'fumadocs-core/source';
import { pageSchema } from 'fumadocs-core/source/schema';
import { defineCollections } from 'fumadocs-mdx/macro';

import { imageUrls } from '@/lib/images';
import { type Locale, baseLocale, locales } from '@/paraglide/runtime';

const legalDocs = defineCollections({
  type: 'doc',
  dir: 'content/legal',
  lastModified: true,
  postprocess: { includeProcessedMarkdown: true },
  schema: pageSchema.extend({
    published: z.coerce.date().transform((value) => value.toISOString()),
    // Reading order on the index; unnumbered documents fall to the end, alphabetically.
    order: z.number().default(Number.MAX_SAFE_INTEGER),
  }),
});

const policyDocs = defineCollections({
  type: 'doc',
  dir: 'content/policies',
  lastModified: true,
  postprocess: { includeProcessedMarkdown: true },
  schema: pageSchema.extend({
    published: z.coerce.date().transform((value) => value.toISOString()),
    // Reading order on the index; unnumbered documents fall to the end, alphabetically.
    order: z.number().default(Number.MAX_SAFE_INTEGER),
  }),
});

const newsDocs = defineCollections({
  type: 'doc',
  dir: 'content/news',
  async: true,
  lastModified: true,
  postprocess: { includeProcessedMarkdown: true },
  schema: pageSchema.extend({
    date: z.coerce.date().transform((value) => value.toISOString()),
    source: z.object({ name: z.string(), url: z.url().optional() }).optional(),
    tags: z.string().array().optional(),
    // A file name relative to the article in the source; `resolveImages` below turns it into the
    // URL Vite emitted, so by the time anything reads it, it can go straight into an `<img>`.
    cover: z.string().optional(),
  }),
});

const careerDocs = defineCollections({
  type: 'doc',
  dir: 'content/careers',
  lastModified: true,
  schema: pageSchema.extend({
    // Shown as tags on the listing and the post, so keep them short.
    type: z.enum(['Freelance', 'Full time', 'Part time', 'Contract']),
    location: z.string(),
    // Reads "£450 per day" on the page and is Google's base salary; a pair of amounts is a range.
    pay: z
      .object({
        amount: z.union([z.int().positive(), z.tuple([z.int().positive(), z.int().positive()])]),
        currency: z.currency(),
        per: z.enum(['hour', 'day', 'week', 'month', 'year']),
      })
      .optional(),
    posted: z.coerce.date().transform((value) => value.toISOString()),
    // Open through this day. From the next build after it, the role shows as closed.
    closes: z.coerce.date().transform((value) => value.toISOString()),
    // Filled before its closing date; shows as closed from the next build.
    filled: z.boolean().default(false),
    // The matching LinkedIn post, when the role is also advertised there.
    linkedin: z.url().optional(),
    // Drafts render in development only, so a post can be reviewed before it goes live.
    draft: z.boolean().default(false),
  }),
});

const RELATIVE_IMAGE = /^\.{1,2}\/.*\.(?:jpe?g|png|webp|avif|gif)$/i;

/**
 * Turns the images a document names in its front matter into the URLs Vite emitted. It matches on
 * the value, not the field, so the field can be called anything and there can be more than one.
 */
function resolveImages(dir: string): LoaderPlugin {
  return {
    name: 'awpmarine:content-images',
    transformStorage({ storage }) {
      for (const file of storage.files.values()) {
        if (file.format !== 'page') continue;

        const slash = file.path.lastIndexOf('/');
        const base = `../../content/${dir}/${slash === -1 ? '' : file.path.slice(0, slash + 1)}`;
        const data = file.data as Record<string, unknown>;

        for (const [field, value] of Object.entries(data)) {
          if (typeof value !== 'string' || !RELATIVE_IMAGE.test(value)) continue;
          data[field] = imageUrls[base + value.replace(/^\.\//, '')];
        }
      }
    },
  };
}

// A translation sits beside its original, as `privacy.zh.md`; a page without one falls back to English.
const i18n = defineI18n({ languages: [...locales], defaultLanguage: baseLocale, parser: 'dot' });

/** URLs without a locale prefix: the router adds it to links, and the head to canonical URLs. */
const unprefixed = (baseUrl: string) => (slugs: string[]) => [baseUrl, ...slugs].join('/');

export const legal = loader({
  source: legalDocs.toFumadocsSource(),
  baseUrl: '/legal',
  url: unprefixed('/legal'),
  i18n,
  plugins: [resolveImages('legal')],
});
export const policies = loader({
  source: policyDocs.toFumadocsSource(),
  baseUrl: '/legal/policies',
  url: unprefixed('/legal/policies'),
  i18n,
  plugins: [resolveImages('policies')],
});
export const news = loader({
  source: newsDocs.toFumadocsSource(),
  baseUrl: '/news',
  url: unprefixed('/news'),
  i18n,
  plugins: [resolveImages('news')],
});

export const careers = loader({
  source: careerDocs.toFumadocsSource(),
  baseUrl: '/careers',
  url: unprefixed('/careers'),
  i18n,
});

type ContentPage = { locale?: string; path: string };

/** Whether a page is written in the locale it was asked for, rather than standing in from English. */
export function isTranslated(page: ContentPage) {
  return page.locale === baseLocale || /\.(\w+)\.mdx?$/.exec(page.path)?.[1] === page.locale;
}

/** The locales a page has been written in, which are the only ones search engines should be pointed at. */
export function translatedLocales(
  source: { getPage: (slugs: string[], language?: string) => ContentPage | undefined },
  slugs: string[],
): Locale[] {
  return locales.filter((locale) => {
    const page = source.getPage(slugs, locale);
    return page !== undefined && isTranslated(page);
  });
}

export { careerDocs, legalDocs, newsDocs, policyDocs };
